import Phaser from 'phaser';
import { INK } from '../../assets/palette.ts';

const INK_NUM = Phaser.Display.Color.HexStringToColor(INK).color;
const DOT_R = 13;
const GAP = 34;
const PER_ROW = 5;

/**
 * A row (or two) of dots showing a quantity — the visual companion to the
 * spoken number, so a pre-reader sees "how many" as a pattern. Laid out like a
 * ten-frame (up to five per row) which makes small quantities instantly
 * recognizable.
 */
export class CountDots extends Phaser.GameObjects.Container {
  private g: Phaser.GameObjects.Graphics;
  private color: number;

  constructor(scene: Phaser.Scene, x: number, y: number, color = INK_NUM) {
    super(scene, x, y);
    this.color = color;
    this.g = scene.add.graphics();
    this.add(this.g);
    scene.add.existing(this);
  }

  setColor(color: number): void {
    this.color = color;
  }

  /** Draw `n` filled dots, centered on this container's origin. */
  setCount(n: number): void {
    this.g.clear();
    if (n <= 0) return;
    const rows = Math.ceil(n / PER_ROW);
    let placed = 0;
    for (let r = 0; r < rows; r++) {
      const inRow = Math.min(PER_ROW, n - placed);
      const rowW = (inRow - 1) * GAP;
      const startX = -rowW / 2;
      const y = (r - (rows - 1) / 2) * GAP;
      for (let c = 0; c < inRow; c++) {
        this.g.fillStyle(this.color, 1);
        this.g.fillCircle(startX + c * GAP, y, DOT_R);
        // Dark outline so pale flavors (vanilla, cookies) still read on the white bubble.
        this.g.lineStyle(3, INK_NUM, 0.85);
        this.g.strokeCircle(startX + c * GAP, y, DOT_R);
        placed++;
      }
    }
  }

  /** Briefly pop the most recent dot count for emphasis while counting. */
  pulse(): void {
    this.scene.tweens.add({
      targets: this,
      scale: { from: 1.25, to: 1 },
      duration: 240,
      ease: 'Back.Out',
    });
  }
}
